/**
 * WebSocket message schemas for the quest/* RPC family.
 *
 * Backward-compat rule (CLAUDE.md): every new field on outbound messages is
 * `.optional()`. The quest/* family is brand-new — old clients will simply
 * ignore these messages; new clients can safely send them to old daemons (the
 * inbound discriminated union will reject unknown types with a clean error
 * rather than crashing).
 */

import { z } from "zod";
import { AgentProviderSchema } from "../agent/provider-manifest.js";
import {
  QuestKindSchema,
  QuestRecordSchema,
  QuestStatusSchema,
  QuestTerminationSchema,
} from "./types.js";

// ---------------------------------------------------------------------------
// Per-card config sent at run-time (subset of QuestCard the client owns)
// ---------------------------------------------------------------------------

export const QuestCardConfigInputSchema = z.object({
  role: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  prompt: z.string().nullable().optional(),
});

// ---------------------------------------------------------------------------
// Inbound requests
// ---------------------------------------------------------------------------

export const QuestRunRequestSchema = z.object({
  type: z.literal("quest/run"),
  requestId: z.string(),
  kind: QuestKindSchema,
  cwd: z.string(),
  prompt: z.string().trim().min(1),
  defaultProvider: AgentProviderSchema.optional(),
  defaultModel: z.string().trim().min(1).optional(),
  termination: QuestTerminationSchema,
  /** Per-card overrides (length must match termination.n for "count"). Optional. */
  cards: z.array(QuestCardConfigInputSchema).optional(),
  summarizerEnabled: z.boolean().optional(),
});

export const QuestListRequestSchema = z.object({
  type: z.literal("quest/list"),
  requestId: z.string(),
});

export const QuestInspectRequestSchema = z.object({
  type: z.literal("quest/inspect"),
  requestId: z.string(),
  id: z.string().trim().min(1),
});

export const QuestStopRequestSchema = z.object({
  type: z.literal("quest/stop"),
  requestId: z.string(),
  id: z.string().trim().min(1),
});

// ---------------------------------------------------------------------------
// Outbound responses
// ---------------------------------------------------------------------------

export const QuestListItemSchema = z.object({
  id: z.string(),
  kind: QuestKindSchema,
  status: QuestStatusSchema,
  cwd: z.string(),
  prompt: z.string(),
  /** Auto-generated short label. Optional for backward compat with old daemons. */
  name: z.string().optional().default(""),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const QuestRunResponseSchema = z.object({
  type: z.literal("quest/run/response"),
  payload: z.object({
    requestId: z.string(),
    quest: QuestRecordSchema.nullable(),
    error: z.string().nullable(),
  }),
});

export const QuestListResponseSchema = z.object({
  type: z.literal("quest/list/response"),
  payload: z.object({
    requestId: z.string(),
    quests: z.array(QuestListItemSchema),
    error: z.string().nullable(),
  }),
});

export const QuestInspectResponseSchema = z.object({
  type: z.literal("quest/inspect/response"),
  payload: z.object({
    requestId: z.string(),
    quest: QuestRecordSchema.nullable(),
    error: z.string().nullable(),
  }),
});

export const QuestStopResponseSchema = z.object({
  type: z.literal("quest/stop/response"),
  payload: z.object({
    requestId: z.string(),
    quest: QuestRecordSchema.nullable(),
    error: z.string().nullable(),
  }),
});

/** Pushed whenever a quest record changes (card status, summary, etc.). */
export const QuestStreamMessageSchema = z.object({
  type: z.literal("quest/stream"),
  questId: z.string(),
  quest: QuestRecordSchema,
});

// ---------------------------------------------------------------------------
// Roles — RPC schemas (list, create, write-frontmatter)
// ---------------------------------------------------------------------------

export const RoleScopeSchema = z.enum(["global", "project"]);

export const RoleEntrySchema = z.object({
  id: z.string(),
  scope: RoleScopeSchema,
  /** Category subfolder inside .roles/, or null for flat files. */
  category: z.string().nullable().optional(),
  name: z.string(),
  description: z.string(),
  /** Trigger words from the `trigger-words` frontmatter key. */
  triggerWords: z.array(z.string()).optional().default([]),
  provider: z.string(),
  model: z.string(),
  mode: z.string(),
  path: z.string(),
  modifiedAt: z.string(),
  size: z.number(),
});

/** Editable frontmatter fields. Unspecified fields are preserved verbatim. */
export const RoleFrontmatterSchema = z.object({
  description: z.string().optional(),
  triggerWords: z.array(z.string()).optional(),
  allowedTools: z.array(z.string()).optional(),
  provider: z.string().optional(),
  model: z.string().optional(),
  mode: z.string().optional(),
});

// roles/list — legacy merged listing
export const RolesListRequestSchema = z.object({
  type: z.literal("roles/list"),
  requestId: z.string(),
  workspaceRoot: z.string().optional(),
});

export const RolesListResponseSchema = z.object({
  type: z.literal("roles/list/response"),
  payload: z.object({
    requestId: z.string(),
    roles: z.array(RoleEntrySchema),
    error: z.string().nullable(),
  }),
});

// roles/list-scope — per-scope listing used by the Roles modal tabs
export const RolesListScopeRequestSchema = z.object({
  type: z.literal("roles/list-scope"),
  requestId: z.string(),
  scope: RoleScopeSchema,
  workspaceRoot: z.string().optional(),
});

export const RolesListScopeResponseSchema = z.object({
  type: z.literal("roles/list-scope/response"),
  payload: z.object({
    requestId: z.string(),
    roles: z.array(RoleEntrySchema),
    error: z.string().nullable(),
  }),
});

// roles/create — create a new role file with starter template
export const RoleCreateRequestSchema = z.object({
  type: z.literal("roles/create"),
  requestId: z.string(),
  scope: RoleScopeSchema,
  name: z.string(),
  category: z.string().optional(),
  workspaceRoot: z.string().optional(),
});

export const RoleCreateResponseSchema = z.object({
  type: z.literal("roles/create/response"),
  payload: z.object({
    requestId: z.string(),
    /** Absolute path of the created .md file. Empty string when error is set. */
    path: z.string(),
    error: z.string().nullable(),
  }),
});

// roles/write-frontmatter — replace ONLY the frontmatter block (body preserved)
export const RoleWriteFrontmatterRequestSchema = z.object({
  type: z.literal("roles/write-frontmatter"),
  requestId: z.string(),
  /** Absolute path inside an allowlisted role root. */
  path: z.string(),
  workspaceRoot: z.string().optional(),
  frontmatter: RoleFrontmatterSchema,
});

export const RoleWriteFrontmatterResponseSchema = z.object({
  type: z.literal("roles/write-frontmatter/response"),
  payload: z.object({
    requestId: z.string(),
    path: z.string(),
    error: z.string().nullable(),
  }),
});

// roles/move — rename and/or change category (file move + frontmatter update)
export const RoleMoveRequestSchema = z.object({
  type: z.literal("roles/move"),
  requestId: z.string(),
  path: z.string(),
  newName: z.string().optional(),
  newCategory: z.string().optional(),
  workspaceRoot: z.string().optional(),
});

export const RoleMoveResponseSchema = z.object({
  type: z.literal("roles/move/response"),
  payload: z.object({
    requestId: z.string(),
    path: z.string(),
    error: z.string().nullable(),
  }),
});

// ---------------------------------------------------------------------------
// Brands — RPC schemas (list, list-scope, create, write-frontmatter)
// ---------------------------------------------------------------------------

export const BrandScopeSchema = z.literal("project");

export const BrandVariableTypeSchema = z.enum([
  "color",
  "gradient",
  "font",
  "asset",
  "text",
  "number",
  "select",
  "record-list",
]);

export const BrandSectionSchema = z.string();

export const BrandVariableSchema = z.object({
  key: z.string(),
  type: BrandVariableTypeSchema,
  label: z.string(),
  value: z.string(),
  section: BrandSectionSchema.optional(),
  options: z.array(z.string()).optional(),
  fileRef: z.string().optional(),
  locked: z.boolean().optional(),
  enabled: z.boolean().optional(),
});

export const BrandEntrySchema = z.object({
  id: z.string(),
  scope: BrandScopeSchema,
  name: z.string(),
  description: z.string(),
  variables: z.array(BrandVariableSchema).optional().default([]),
  path: z.string(),
  modifiedAt: z.string(),
  size: z.number(),
  /** Raw markdown body (frontmatter stripped). Optional, fed by the scanner. */
  body: z.string().optional(),
});

export const BrandFrontmatterSchema = z.object({
  description: z.string().optional(),
  variables: z.array(BrandVariableSchema).optional(),
  /**
   * Explicit delete signal — dot-paths to remove from the underlying
   * tokens.json before the variable patches are applied. Used by the
   * colors page so a user can remove palette entries (`color.<slug>`)
   * from the UI; safer than delete-by-omission (a partial-list save
   * would otherwise nuke unrelated keys). Only honored by the
   * tokens-writer path; the .md scanner ignores it.
   */
  removedKeys: z.array(z.string()).optional(),
});

// brands/list-scope — list brand section files for the current project
export const BrandsListScopeRequestSchema = z.object({
  type: z.literal("brands/list-scope"),
  requestId: z.string(),
  workspaceRoot: z.string().optional(),
});

export const BrandsListScopeResponseSchema = z.object({
  type: z.literal("brands/list-scope/response"),
  payload: z.object({
    requestId: z.string(),
    brands: z.array(BrandEntrySchema),
    error: z.string().nullable(),
  }),
});

// brands/create — create a new brand section file in .appostle/brand/
export const BrandCreateRequestSchema = z.object({
  type: z.literal("brands/create"),
  requestId: z.string(),
  name: z.string(),
  workspaceRoot: z.string().optional(),
});

export const BrandCreateResponseSchema = z.object({
  type: z.literal("brands/create/response"),
  payload: z.object({
    requestId: z.string(),
    path: z.string(),
    error: z.string().nullable(),
  }),
});

// Companion-asset derivation spec — a "please also generate this from the
// just-saved primary" instruction. Currently only `svg-mono` (flatten fills
// and strokes to a single color); future kinds slot in additively.
export const AssetDerivationSchema = z.object({
  kind: z.literal("svg-mono"),
  /** Target color to flatten all fills / strokes to (e.g. "#000", "#fff"). */
  color: z.string(),
  /** Bare filename stem for the derived asset. */
  targetName: z.string(),
});

const AssetDerivationResultSchema = z.object({
  targetName: z.string(),
  relativePath: z.string(),
  absolutePath: z.string(),
  error: z.string().nullable(),
});

// brands/asset-copy — copy a host file into <workspaceRoot>/.appostle/brand/assets/[category]/
export const BrandAssetCopyRequestSchema = z.object({
  type: z.literal("brands/asset-copy"),
  requestId: z.string(),
  workspaceRoot: z.string(),
  sourcePath: z.string(),
  targetName: z.string(),
  /** Optional asset category subfolder ("logo", "typefaces", …). */
  category: z.string().optional(),
  /** Optional companion derivations to run after the primary save. */
  derive: z.array(AssetDerivationSchema).optional(),
});

// brands/asset-upload — upload raw file bytes into <workspaceRoot>/.appostle/brand/assets/[category]/
export const BrandAssetUploadRequestSchema = z.object({
  type: z.literal("brands/asset-upload"),
  requestId: z.string(),
  workspaceRoot: z.string(),
  targetName: z.string(),
  sourceName: z.string().optional(),
  dataBase64: z.string(),
  /** Optional asset category subfolder ("logo", "typefaces", …). */
  category: z.string().optional(),
  /** Optional companion derivations to run after the primary save. */
  derive: z.array(AssetDerivationSchema).optional(),
});

export const BrandAssetUploadResponseSchema = z.object({
  type: z.literal("brands/asset-upload/response"),
  payload: z.object({
    requestId: z.string(),
    relativePath: z.string(),
    absolutePath: z.string(),
    derived: z.array(AssetDerivationResultSchema).optional().default([]),
    error: z.string().nullable(),
  }),
});

export const BrandAssetCopyResponseSchema = z.object({
  type: z.literal("brands/asset-copy/response"),
  payload: z.object({
    requestId: z.string(),
    relativePath: z.string(),
    absolutePath: z.string(),
    derived: z.array(AssetDerivationResultSchema).optional().default([]),
    error: z.string().nullable(),
  }),
});

// brands/asset-delete — drop a single asset file from <workspaceRoot>/.appostle/brand/assets/
export const BrandAssetDeleteRequestSchema = z.object({
  type: z.literal("brands/asset-delete"),
  requestId: z.string(),
  workspaceRoot: z.string(),
  /** Path relative to `.appostle/brand/`, e.g. "assets/shape/circular-text.svg". */
  relativePath: z.string(),
});

export const BrandAssetDeleteResponseSchema = z.object({
  type: z.literal("brands/asset-delete/response"),
  payload: z.object({
    requestId: z.string(),
    error: z.string().nullable(),
  }),
});

// brands/asset-rename — rename a single asset file in place (same dir, same ext)
export const BrandAssetRenameRequestSchema = z.object({
  type: z.literal("brands/asset-rename"),
  requestId: z.string(),
  workspaceRoot: z.string(),
  /** Path relative to `.appostle/brand/`. */
  fromRelativePath: z.string(),
  /** Bare filename stem (no extension) for the new name. */
  toTargetName: z.string(),
});

export const BrandAssetRenameResponseSchema = z.object({
  type: z.literal("brands/asset-rename/response"),
  payload: z.object({
    requestId: z.string(),
    relativePath: z.string(),
    absolutePath: z.string(),
    error: z.string().nullable(),
  }),
});

// brands/write-frontmatter — update variables and metadata
export const BrandWriteFrontmatterRequestSchema = z.object({
  type: z.literal("brands/write-frontmatter"),
  requestId: z.string(),
  path: z.string(),
  workspaceRoot: z.string().optional(),
  frontmatter: BrandFrontmatterSchema,
});

export const BrandWriteFrontmatterResponseSchema = z.object({
  type: z.literal("brands/write-frontmatter/response"),
  payload: z.object({
    requestId: z.string(),
    path: z.string(),
    error: z.string().nullable(),
  }),
});

// brands/generate-tokens — spawn an internal agent (Haiku → Codex mini →
// OpenCode nano fallback) that fills the design tokens from the palette.
// Locked tokens are honoured server-side; the agent never sees them.
export const BrandGenerateTokensRequestSchema = z.object({
  type: z.literal("brands/generate-tokens"),
  requestId: z.string(),
  workspaceRoot: z.string(),
  brandPath: z.string(),
  mode: z.enum(["auto", "prompt"]),
  prompt: z.string().optional(),
});

export const BrandGenerateTokensResponseSchema = z.object({
  type: z.literal("brands/generate-tokens/response"),
  payload: z.object({
    requestId: z.string(),
    generatedCount: z.number(),
    error: z.string().nullable(),
  }),
});

// brands/token-generation-status — lightweight probe so a re-mounted modal
// can tell that the brand-token internal agent is still running for this
// workspace. The generator is a daemon-side internal agent; once spawned it
// lives independently of the UI, so the modal needs an out-of-band way to
// detect "still busy" after the originating component unmounts.
export const BrandTokenGenerationStatusRequestSchema = z.object({
  type: z.literal("brands/token-generation-status"),
  requestId: z.string(),
  workspaceRoot: z.string(),
  brandPath: z.string(),
});

export const BrandTokenGenerationStatusResponseSchema = z.object({
  type: z.literal("brands/token-generation-status/response"),
  payload: z.object({
    requestId: z.string(),
    active: z.boolean(),
    error: z.string().nullable(),
  }),
});

// brands/generate-layout — spawn an internal agent that fills the
// layout fields from a user's freeform prompt.
export const BrandGenerateLayoutRequestSchema = z.object({
  type: z.literal("brands/generate-layout"),
  requestId: z.string(),
  workspaceRoot: z.string(),
  brandPath: z.string(),
  prompt: z.string(),
});

export const BrandGenerateLayoutResponseSchema = z.object({
  type: z.literal("brands/generate-layout/response"),
  payload: z.object({
    requestId: z.string(),
    roleContent: z.string().optional(),
    error: z.string().nullable(),
  }),
});

// ---------------------------------------------------------------------------
// brands/layout-qa/next — conversational Q&A flow for layout generation
// ---------------------------------------------------------------------------

export const LayoutQaMessageSchema = z.object({
  role: z.enum(["assistant", "user"]),
  content: z.string(),
});

export const BrandLayoutQaNextRequestSchema = z.object({
  type: z.literal("brands/layout-qa/next"),
  requestId: z.string(),
  workspaceRoot: z.string(),
  brandPath: z.string(),
  /** Full conversation so far (assistant questions + user answers). Empty to start. */
  conversation: z.array(LayoutQaMessageSchema),
});

export const BrandLayoutQaNextResponseSchema = z.object({
  type: z.literal("brands/layout-qa/next/response"),
  payload: z.object({
    requestId: z.string(),
    /** false = here's the next question; true = Q&A complete, role document written */
    done: z.boolean(),
    /** The next question to show the user (when done=false). */
    question: z.string().optional(),
    /** Answer options for the current question (when done=false). */
    options: z.array(z.string()).optional(),
    /** The generated role document content (when done=true). */
    roleContent: z.string().optional(),
    error: z.string().nullable(),
  }),
});

// ---------------------------------------------------------------------------
// brands/generate-wireframe — generate WireframeData JSON from all brand files
// ---------------------------------------------------------------------------

export const BrandGenerateWireframeRequestSchema = z.object({
  type: z.literal("brands/generate-wireframe"),
  requestId: z.string(),
  workspaceRoot: z.string(),
  brandPath: z.string(),
});

export const BrandGenerateWireframeResponseSchema = z.object({
  type: z.literal("brands/generate-wireframe/response"),
  payload: z.object({
    requestId: z.string(),
    /** JSON string of WireframeData. */
    wireframe: z.string().optional(),
    error: z.string().nullable(),
  }),
});

export type LayoutQaMessage = z.infer<typeof LayoutQaMessageSchema>;
export type BrandLayoutQaNextRequest = z.infer<typeof BrandLayoutQaNextRequestSchema>;
export type BrandLayoutQaNextResponse = z.infer<typeof BrandLayoutQaNextResponseSchema>;
export type BrandGenerateWireframeRequest = z.infer<typeof BrandGenerateWireframeRequestSchema>;
export type BrandGenerateWireframeResponse = z.infer<typeof BrandGenerateWireframeResponseSchema>;

// brands/analyze-photography — spawn an internal agent that analyzes
// reference photos + brief and fills the photography.md camera dials.
export const BrandAnalyzePhotographyRequestSchema = z.object({
  type: z.literal("brands/analyze-photography"),
  requestId: z.string(),
  workspaceRoot: z.string(),
  brandPath: z.string(),
  brief: z.string(),
  referenceDescriptions: z.array(z.string()).optional(),
});

export const BrandAnalyzePhotographyResponseSchema = z.object({
  type: z.literal("brands/analyze-photography/response"),
  payload: z.object({
    requestId: z.string(),
    updatedCount: z.number(),
    error: z.string().nullable(),
  }),
});

export type BrandAnalyzePhotographyRequest = z.infer<typeof BrandAnalyzePhotographyRequestSchema>;
export type BrandAnalyzePhotographyResponse = z.infer<typeof BrandAnalyzePhotographyResponseSchema>;

export type BrandScope = z.infer<typeof BrandScopeSchema>;
export type BrandVariableType = z.infer<typeof BrandVariableTypeSchema>;
export type BrandVariable = z.infer<typeof BrandVariableSchema>;
export type BrandEntry = z.infer<typeof BrandEntrySchema>;
export type BrandFrontmatter = z.infer<typeof BrandFrontmatterSchema>;
export type BrandsListScopeRequest = z.infer<typeof BrandsListScopeRequestSchema>;
export type BrandsListScopeResponse = z.infer<typeof BrandsListScopeResponseSchema>;
export type BrandCreateRequest = z.infer<typeof BrandCreateRequestSchema>;
export type BrandCreateResponse = z.infer<typeof BrandCreateResponseSchema>;
export type BrandWriteFrontmatterRequest = z.infer<typeof BrandWriteFrontmatterRequestSchema>;
export type BrandWriteFrontmatterResponse = z.infer<typeof BrandWriteFrontmatterResponseSchema>;
export type BrandAssetCopyRequest = z.infer<typeof BrandAssetCopyRequestSchema>;
export type BrandAssetCopyResponse = z.infer<typeof BrandAssetCopyResponseSchema>;
export type BrandAssetUploadRequest = z.infer<typeof BrandAssetUploadRequestSchema>;
export type BrandAssetUploadResponse = z.infer<typeof BrandAssetUploadResponseSchema>;
export type BrandAssetDeleteRequest = z.infer<typeof BrandAssetDeleteRequestSchema>;
export type BrandAssetDeleteResponse = z.infer<typeof BrandAssetDeleteResponseSchema>;
export type BrandAssetRenameRequest = z.infer<typeof BrandAssetRenameRequestSchema>;
export type BrandAssetRenameResponse = z.infer<typeof BrandAssetRenameResponseSchema>;
export type BrandGenerateTokensRequest = z.infer<typeof BrandGenerateTokensRequestSchema>;
export type BrandGenerateTokensResponse = z.infer<typeof BrandGenerateTokensResponseSchema>;
export type BrandGenerateLayoutRequest = z.infer<typeof BrandGenerateLayoutRequestSchema>;
export type BrandGenerateLayoutResponse = z.infer<typeof BrandGenerateLayoutResponseSchema>;

// ---------------------------------------------------------------------------
// RightFont — read-only access to the local RightFont font library database
// ---------------------------------------------------------------------------

const RightFontEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  postscriptName: z.string(),
  family: z.string(),
  familyGroup: z.string(),
  style: z.string(),
  weight: z.number(),
  weightValue: z.number(),
  category: z.string(),
  format: z.string(),
  absolutePath: z.string(),
  location: z.string(),
  starred: z.boolean(),
});

const RightFontFamilySchema = z.object({
  familyGroup: z.string(),
  category: z.string(),
  fonts: z.array(RightFontEntrySchema),
});

const RightFontCollectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  fontCount: z.number(),
  fontIds: z.array(z.string()),
});

export const RightFontLibraryRequestSchema = z.object({
  type: z.literal("rightfont/library"),
  requestId: z.string(),
  libraryPath: z.string(),
});

export const RightFontLibraryResponseSchema = z.object({
  type: z.literal("rightfont/library/response"),
  payload: z.object({
    requestId: z.string(),
    families: z.array(RightFontFamilySchema).default([]),
    collections: z.array(RightFontCollectionSchema).default([]),
    totalFonts: z.number().default(0),
    error: z.string().nullable(),
  }),
});

export type RightFontLibraryRequest = z.infer<typeof RightFontLibraryRequestSchema>;
export type RightFontLibraryResponse = z.infer<typeof RightFontLibraryResponseSchema>;

// ---------------------------------------------------------------------------
// Google Fonts — browse and download from the Google Fonts catalog
// ---------------------------------------------------------------------------

const GoogleFontVariantSchema = z.object({
  weight: z.string(),
  italic: z.boolean(),
});

const GoogleFontFamilySchema = z.object({
  family: z.string(),
  category: z.string(),
  variants: z.array(GoogleFontVariantSchema),
  popularity: z.number(),
  designers: z.array(z.string()).default([]),
  dateAdded: z.string().optional(),
  lastModified: z.string().optional(),
});

export const GoogleFontsCatalogRequestSchema = z.object({
  type: z.literal("google-fonts/catalog"),
  requestId: z.string(),
});

export const GoogleFontsCatalogResponseSchema = z.object({
  type: z.literal("google-fonts/catalog/response"),
  payload: z.object({
    requestId: z.string(),
    families: z.array(GoogleFontFamilySchema).default([]),
    totalFonts: z.number().default(0),
    error: z.string().nullable(),
  }),
});

export const GoogleFontsDownloadRequestSchema = z.object({
  type: z.literal("google-fonts/download"),
  requestId: z.string(),
  workspaceRoot: z.string(),
  family: z.string(),
  weight: z.string(),
  italic: z.boolean().optional(),
  targetName: z.string(),
});

export const GoogleFontsDownloadResponseSchema = z.object({
  type: z.literal("google-fonts/download/response"),
  payload: z.object({
    requestId: z.string(),
    relativePath: z.string(),
    absolutePath: z.string(),
    error: z.string().nullable(),
  }),
});

export type GoogleFontsCatalogRequest = z.infer<typeof GoogleFontsCatalogRequestSchema>;
export type GoogleFontsCatalogResponse = z.infer<typeof GoogleFontsCatalogResponseSchema>;
export type GoogleFontsDownloadRequest = z.infer<typeof GoogleFontsDownloadRequestSchema>;
export type GoogleFontsDownloadResponse = z.infer<typeof GoogleFontsDownloadResponseSchema>;

export type QuestCardConfigInput = z.infer<typeof QuestCardConfigInputSchema>;
export type QuestRunRequest = z.infer<typeof QuestRunRequestSchema>;
export type QuestListRequest = z.infer<typeof QuestListRequestSchema>;
export type QuestInspectRequest = z.infer<typeof QuestInspectRequestSchema>;
export type QuestStopRequest = z.infer<typeof QuestStopRequestSchema>;
export type QuestListItem = z.infer<typeof QuestListItemSchema>;
export type QuestRunResponse = z.infer<typeof QuestRunResponseSchema>;
export type QuestListResponse = z.infer<typeof QuestListResponseSchema>;
export type QuestInspectResponse = z.infer<typeof QuestInspectResponseSchema>;
export type QuestStopResponse = z.infer<typeof QuestStopResponseSchema>;
export type QuestStreamMessage = z.infer<typeof QuestStreamMessageSchema>;
export type RoleScope = z.infer<typeof RoleScopeSchema>;
export type RoleEntry = z.infer<typeof RoleEntrySchema>;
export type RoleFrontmatter = z.infer<typeof RoleFrontmatterSchema>;
export type RolesListRequest = z.infer<typeof RolesListRequestSchema>;
export type RolesListResponse = z.infer<typeof RolesListResponseSchema>;
export type RolesListScopeRequest = z.infer<typeof RolesListScopeRequestSchema>;
export type RolesListScopeResponse = z.infer<typeof RolesListScopeResponseSchema>;
export type RoleCreateRequest = z.infer<typeof RoleCreateRequestSchema>;
export type RoleCreateResponse = z.infer<typeof RoleCreateResponseSchema>;
export type RoleWriteFrontmatterRequest = z.infer<typeof RoleWriteFrontmatterRequestSchema>;
export type RoleWriteFrontmatterResponse = z.infer<typeof RoleWriteFrontmatterResponseSchema>;
export type RoleMoveRequest = z.infer<typeof RoleMoveRequestSchema>;
export type RoleMoveResponse = z.infer<typeof RoleMoveResponseSchema>;
